import { Link } from "react-router-dom";
import MediaFrame from "@/components/feature/MediaFrame";
import Reveal from "@/components/feature/Reveal";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { clinic } from "@/mocks/clinic";
import type { InjectablesDetail } from "@/mocks/injectablesPages";

/**
 * 注入治療カテゴリ詳細ページ（BOTOX / ヒアルロン酸）共通のHERO。
 * NOTE: FV画像はページごとに専用ビジュアルを1対1で表示する（親ページや他ページと使い回さない）。
 *       枠は親ページと揃えて 3:2 とし、画像の縦横比を崩さず人物が不自然に切れないよう調整する。
 *       カテゴリの世界観は Charcoal / Ivory / Warm White をベースに、
 *       トープ〜チャコールベージュを効かせて高級感・精密さ・落ち着きを表現する。
 */
export default function InjectablesSubHero({ data }: { data: InjectablesDetail }) {
  return (
    <section className="relative overflow-hidden border-b border-background-300/70 bg-background-50 pb-16 pt-28 md:pb-20 md:pt-36 lg:pb-24 lg:pt-40">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-24 h-72 w-72 rounded-full bg-secondary-100/60 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full border border-foreground-950/5"
      />

      <div className="u-container relative">
        {/* 施術詳細：HOME ＞ 親カテゴリ ＞ 現在の施術（現在ページは非リンク） */}
        <Breadcrumb
          items={[
            { label: "HOME", href: "/", path: "/" },
            { label: "注入治療", href: "/treatment/injectables", path: "/treatment/injectables" },
            { label: data.jp, path: data.path },
          ]}
        />

        <div className="mt-10 grid grid-cols-1 gap-12 lg:mt-14 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16">
          <div>
            <Reveal>
              <span className="en-eyebrow block text-[11px] text-secondary-700 md:text-[12px]">INJECTABLES</span>
              <span className="mt-5 block h-px w-12 bg-accent-500" />
              <h1 className="mt-7 font-heading text-[32px] leading-[1.5] tracking-[0.02em] text-foreground-950 md:text-[44px]">
                {data.jp}
              </h1>
              <p className="mt-4 text-[12.5px] tracking-[0.18em] text-foreground-500 md:text-[13px]">{data.en}</p>
              <p className="mt-8 font-heading text-[20px] leading-[1.9] text-foreground-900 md:text-[26px]">
                {data.heroMain}
              </p>
              <p className="mt-6 text-[14px] leading-[2.2] text-foreground-700 md:text-[15px]">{data.heroSub}</p>
            </Reveal>

            <Reveal delay={140}>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12px] text-foreground-600">
                <Link
                  to="/treatment/injectables"
                  className="group inline-flex items-center gap-2 whitespace-nowrap text-foreground-800 transition-colors hover:text-accent-700"
                >
                  <i className="ri-arrow-left-line transition-transform group-hover:-translate-x-0.5" aria-hidden />
                  注入治療へ戻る
                </Link>
                <span className="hidden h-4 w-px bg-foreground-950/15 sm:block" />
                <span className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-accent-700" aria-hidden />
                  渋谷駅近く
                </span>
                <span className="hidden h-4 w-px bg-foreground-950/15 sm:block" />
                <span className="flex items-center gap-2">
                  <i className="ri-time-line text-accent-700" aria-hidden />
                  診療時間 {clinic.hours}
                </span>
              </div>
            </Reveal>
          </div>

          <Reveal delay={120}>
            <MediaFrame
              src={data.heroImage}
              alt={data.heroImageAlt}
              tone="greige"
              icon="ri-image-line"
              label="TREATMENT VISUAL"
              note={data.heroNote}
              objectPosition={data.heroObjectPosition ?? "object-center"}
              className="aspect-[3/2] w-full"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}